import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormControl } from '@angular/forms';
import {
  EConfigurationChannel,
  ESettingInputType,
  IConfiguration,
  IConfigurationTableRow,
} from '@web/dtos';
import { TooltipComponent } from '../../../../components/feedback/tooltip/tooltip.component';
import { ConfigurationControlValue } from '../../../../entities/configuration-entity';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import { ConfigurationChannelMenuComponent } from '../configuration-channel-menu/configuration-channel-menu.component';
import { ConfigurationSettingInputComponent } from '../configuration-setting-input/configuration-setting-input.component';

@Component({
  selector: 'aw-configuration-table-row',
  imports: [
    ConfigurationChannelMenuComponent,
    ConfigurationSettingInputComponent,
    NgClass,
    TooltipComponent,
  ],
  templateUrl: './configuration-table-row.component.html',
})
export class ConfigurationTableRowComponent {
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly tooltipHoverDelayMs: number = 500;

  readonly row: InputSignal<IConfigurationTableRow> =
    input.required<IConfigurationTableRow>();
  readonly showWeb: InputSignal<boolean> = input.required<boolean>();
  readonly showApp: InputSignal<boolean> = input.required<boolean>();
  readonly hasSingleActiveLicenseChannel: InputSignal<boolean> =
    input.required<boolean>();
  readonly sharedControl: InputSignal<
    FormControl<ConfigurationControlValue> | undefined
  > = input<FormControl<ConfigurationControlValue>>();
  readonly webControl: InputSignal<
    FormControl<ConfigurationControlValue> | undefined
  > = input<FormControl<ConfigurationControlValue>>();
  readonly appControl: InputSignal<
    FormControl<ConfigurationControlValue> | undefined
  > = input<FormControl<ConfigurationControlValue>>();
  readonly mergeMenuOpen: InputSignal<boolean> = input<boolean>(false);

  readonly settingLinkToggled: OutputEmitterRef<void> = output<void>();
  readonly mergeMenuClosed: OutputEmitterRef<void> = output<void>();
  readonly mergeChannelSelected: OutputEmitterRef<EConfigurationChannel> =
    output<EConfigurationChannel>();

  protected readonly singleColumnSetting: Signal<IConfiguration | undefined> =
    computed((): IConfiguration | undefined => {
      const row: IConfigurationTableRow = this.row();
      if (row.shared) {
        return row.shared;
      }

      if (!this.hasSingleActiveLicenseChannel()) {
        return undefined;
      }

      return this.showWeb() ? row.web : row.app;
    });
  protected readonly singleColumnControl: Signal<
    FormControl<ConfigurationControlValue> | undefined
  > = computed((): FormControl<ConfigurationControlValue> | undefined => {
    const setting: IConfiguration | undefined = this.singleColumnSetting();
    const row: IConfigurationTableRow = this.row();

    if (!setting) {
      return undefined;
    }

    if (setting === row.shared) {
      return this.sharedControl();
    }

    return setting === row.web ? this.webControl() : this.appControl();
  });

  protected readonly isSettingLinked: Signal<boolean> = computed(
    (): boolean => this.row().shared !== undefined
  );
  protected readonly hasDisabledSettingLink: Signal<boolean> = computed(
    (): boolean => {
      const row: IConfigurationTableRow = this.row();
      if (row.shared) {
        return row.shared.isDisabled === true;
      }

      return (row.web?.isDisabled === true) !== (row.app?.isDisabled === true);
    }
  );
  protected readonly singleProductVisible: Signal<boolean> = computed(
    (): boolean => this.showWeb() !== this.showApp()
  );
  protected readonly settingLinkDisabled: Signal<boolean> = computed(
    (): boolean => this.singleProductVisible() || this.hasDisabledSettingLink()
  );
  protected readonly settingLinkTooltip: Signal<string> = computed(
    (): string => {
      if (this.hasDisabledSettingLink()) {
        return this.isSettingLinked()
          ? this.cmsTextProvider.configuration.bothProductsUnavailable
          : this.cmsTextProvider.configuration.oneProductUnavailable;
      }

      return this.isSettingLinked()
        ? this.cmsTextProvider.configuration.appliesToBothProducts
        : this.cmsTextProvider.configuration.separateValues;
    }
  );
  protected readonly settingLinkAriaLabel: Signal<string> = computed(
    (): string => {
      const action: string = this.hasDisabledSettingLink()
        ? this.isSettingLinked()
          ? this.cmsTextProvider.configuration.cannotSeparateProducts
          : this.cmsTextProvider.configuration.cannotMergeProducts
        : this.isSettingLinked()
          ? this.cmsTextProvider.configuration.separateForWebAndApp
          : this.cmsTextProvider.configuration.mergeForWebAndApp;

      return `${this.row().label} ${action}`;
    }
  );
  protected isCompactConfigurationInput(
    setting: IConfiguration | undefined
  ): boolean {
    return (
      setting?.isDisabled === true ||
      setting?.inputType === ESettingInputType.Checkbox
    );
  }
}
